import { Stethoscope } from "lucide-react";
import { getAdminBetaDiagnostic, useQuery } from "wasp/client/operations";
import { Card, CardContent } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";

/**
 * Diagnostic par connexion bêta.
 *
 * Complète la carte agrégée, qui répond à « le chantier marche-t-il ? » mais
 * pas à « ce compte-là va-t-il bien ? ». Avec un ou deux bêtas parmi des
 * dizaines d'utilisateurs, un total plateforme noie complètement le signal.
 */
const TradovateBetaDiagnosticCard = () => {
  const t = useTranslation();
  const { data } = useQuery(getAdminBetaDiagnostic);

  const rows = data?.rows ?? [];
  // Retypage a la frontiere : SuperJSON elargit `number` a toute sa union de
  // valeurs serialisables, que ni l'arithmetique ni React n'acceptent.
  const openSockets = Number(data?.openSockets ?? 0);
  const socketCap = Number(data?.socketCap ?? 0);
  const windowStart = data?.windowStart ? new Date(data.windowStart as Date) : null;

  const healthLabel = (health: string): string => {
    switch (health) {
      case "healthy":
        return t.adminDashboard.betaDiagHealthy;
      case "silent":
        return t.adminDashboard.betaDiagSilent;
      case "stale":
        return t.adminDashboard.betaDiagStale;
      case "flapping":
        return t.adminDashboard.betaDiagFlapping;
      case "cut":
        return t.adminDashboard.betaDiagCut;
      case "never_connected":
        return t.adminDashboard.betaDiagNeverConnected;
      case "flag_without_plan":
        return t.adminDashboard.betaDiagFlagWithoutPlan;
      default:
        return t.adminDashboard.betaDiagNotEnrolled;
    }
  };

  // Mode TENU (et non voulu) : un ws_only refusé par le garde d'écriture
  // s'affiche en repli, avec son motif au survol.
  const writeModeLabel = (mode: string | null): string => {
    switch (mode) {
      case "ws_only":
        return t.adminDashboard.betaDiagModeWsOnly;
      case "rest_fallback":
        return t.adminDashboard.betaDiagModeFallback;
      case "rest_only":
        return t.adminDashboard.betaDiagModeRestOnly;
      default:
        return "-";
    }
  };

  // Le vert est réservé au seul état réellement sain. Tout le reste demande une
  // action ou une explication, et l'afficher en neutre inviterait à passer
  // dessus.
  const healthClass = (health: string): string => {
    switch (health) {
      case "healthy":
        return "bg-pnlPositive/15 text-pnlPositive";
      case "cut":
      case "flapping":
      case "silent":
        return "bg-pnlNegative/15 text-pnlNegative";
      case "stale":
        return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]";
      case "flag_without_plan":
        return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]";
      default:
        return "bg-accent text-muted-foreground";
    }
  };

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <Stethoscope className="h-4 w-4" />
        {t.adminDashboard.betaDiagTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.betaDiagHint}
      </p>

      <Card>
        <CardContent className="pt-4">
          {rows.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t.adminDashboard.betaDiagEmpty}
            </p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-4 text-sm">
                <span className="text-muted-foreground">
                  {t.adminDashboard.betaDiagProblems}{" "}
                  <span
                    className={
                      (data?.problemCount ?? 0) > 0
                        ? "text-pnlNegative font-mono font-semibold"
                        : "text-foreground font-mono font-semibold"
                    }
                  >
                    {data?.problemCount ?? 0}
                  </span>
                </span>
                {/* SOCKETS VIVANTS face au plafond du PROCESS
                    (TRADOVATE_WS_MAX_SOCKETS), le seul qui coupe en silence.
                    Colore des 80 % : au-dela, de nouvelles connexions restent
                    en REST sans aucun signal. */}
                <span className="text-muted-foreground">
                  {t.adminDashboard.betaDiagSockets}{" "}
                  <span
                    className={
                      openSockets >= socketCap * 0.8
                        ? "text-pnlNegative font-mono font-semibold"
                        : "text-foreground font-mono font-semibold"
                    }
                  >
                    {openSockets}
                    <span className="text-muted-foreground font-normal">
                      {" / "}
                      {socketCap}
                    </span>
                  </span>
                </span>
                {windowStart && (
                  <span className="text-muted-foreground font-mono text-xs">
                    {t.adminDashboard.betaDiagWindow.replace(
                      "{since}",
                      windowStart.toLocaleString(),
                    )}
                  </span>
                )}
                <span className="text-muted-foreground">
                  {t.adminDashboard.betaDiagSubscribed}{" "}
                  <span className="text-foreground font-mono text-xs">
                    {(data?.subscribedTypes ?? []).join(", ")}
                  </span>
                </span>
              </div>

              {/* Le plafond est PAR UTILISATEUR FINAL chez Tradovate, alors que
                  notre propre garde-fou (TRADOVATE_WS_MAX_SOCKETS) plafonne le
                  process. Confondre les deux ferait lire une marge qui n'existe
                  pas, d'ou ce rappel sous le compteur. */}
              <p className="text-muted-foreground mb-1 text-[10px] italic">
                {t.adminDashboard.betaDiagSocketsHint}
              </p>
              {/* Deux colonnes portent DEUX nombres chacune : sans legende, le
                  second se lit comme une unite du premier. */}
              <p className="text-muted-foreground mb-1 text-[10px] italic">
                {t.adminDashboard.betaDiagControlHint}
              </p>
              <p className="text-muted-foreground mb-1 text-[10px] italic">
                {t.adminDashboard.betaDiagLatencyHint}
              </p>
              <p className="text-muted-foreground mb-4 text-[10px] italic">
                {t.adminDashboard.betaDiagShareHint}
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-left text-xs">
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.betaDiagUser}
                      </th>
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.betaDiagPropFirm}
                      </th>
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.betaDiagHealth}
                      </th>
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.betaDiagWriteMode}
                      </th>
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.betaDiagCadence}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagControl}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagLatency}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagSocketShare}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagFallback}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagEvents}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagReconnects}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagDiffs}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.betaDiagLeaseAge}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={`${r.connectionId}-${r.userEmail}`}
                        className="border-border/50 border-b"
                      >
                        <td className="text-foreground py-2">
                          <span className="flex items-center gap-2">
                            <span className="truncate">{r.userEmail}</span>
                            {/* Etiquette de connexion : sans elle, les N
                                connexions d'un meme utilisateur (multi-prop-firm)
                                donnent des lignes visuellement identiques. Les 8
                                premiers caracteres se recoupent avec les logs
                                serveur. */}
                            <span className="text-muted-foreground bg-accent shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]">
                              {String(r.connectionLabel)}
                            </span>
                          </span>
                        </td>
                        {/* La prop firm est ce que l'utilisateur reconnait :
                            « sk.feca / Jamais connecte » repete trois fois ne
                            dit pas LAQUELLE pose probleme. */}
                        <td className="text-muted-foreground py-2 text-xs">
                          {r.propFirmName ? String(r.propFirmName) : "-"}
                        </td>
                        <td className="py-2">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] ${healthClass(r.health)}`}
                          >
                            {healthLabel(r.health)}
                          </span>
                        </td>
                        {/* QUI ECRIT. Le badge de sante dit que le socket
                            parle, jamais qu'il ecrit : c'etait l'angle mort de
                            la carte. `ws_only` en couleur primaire, parce que
                            c'est le seul mode ou le REST n'est plus un filet. */}
                        <td className="py-2">
                          <span
                            title={r.fallbackReason ? String(r.fallbackReason) : undefined}
                            className={`rounded px-1.5 py-0.5 text-[10px] ${
                              r.effectiveMode === "ws_only"
                                ? "bg-primary/15 text-primary"
                                : r.effectiveMode === "rest_fallback"
                                  ? "bg-pnlNegative/15 text-pnlNegative"
                                  : "bg-accent text-muted-foreground"
                            }`}
                          >
                            {writeModeLabel(r.effectiveMode ? String(r.effectiveMode) : null)}
                          </span>
                        </td>
                        <td className="text-muted-foreground py-2 font-mono text-xs">
                          {r.cadence === "spaced"
                            ? t.adminDashboard.betaDiagCadenceSpaced
                            : r.cadence === "nominal"
                              ? t.adminDashboard.betaDiagCadenceNominal
                              : "-"}
                        </td>
                        {/* Controle quotidien : age, puis trous REPARES. Un
                            trou est en rouge : c'est un trade que le socket
                            avait manque, la seule chose que cet ecran doit
                            faire remonter. */}
                        <td className="py-2 text-right font-mono text-xs">
                          {r.effectiveMode === "ws_only" ? (
                            <>
                              <span
                                className={
                                  r.controlOverdue
                                    ? "text-pnlNegative font-semibold"
                                    : "text-muted-foreground"
                                }
                              >
                                {r.lastControlAgeHours === null
                                  ? t.adminDashboard.betaDiagControlNever
                                  : `${r.lastControlAgeHours} h`}
                              </span>
                              <span
                                className={`ml-2 ${
                                  Number(r.cycleRows24h) > 0
                                    ? "text-pnlNegative font-semibold"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {Number(r.cycleRows24h)}
                              </span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-2 text-right font-mono text-xs">
                          {r.latencyMedianMs === null ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <>
                              <span className="text-foreground">
                                {Number(r.latencyMedianMs)} ms
                              </span>
                              <span className="text-muted-foreground ml-2">
                                {Number(r.realtimeRows24h)}
                              </span>
                            </>
                          )}
                        </td>
                        {/* Part socket : sous 90 % le temps réel laisse passer
                            une part notable des trades au REST. */}
                        <td className="py-2 text-right font-mono text-xs">
                          {r.socketSharePct === null ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <span
                              className={
                                Number(r.socketSharePct) < 90
                                  ? "text-[hsl(var(--warning))]"
                                  : "text-foreground"
                              }
                            >
                              {Number(r.socketSharePct)} %
                            </span>
                          )}
                        </td>
                        {/* Repli : heures approximatives en REST alors que le
                            socket est voulu. Au-delà d'une heure, à creuser. */}
                        <td className="py-2 text-right font-mono text-xs">
                          {r.fallbackHours === null ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <span
                              className={
                                Number(r.fallbackHours) > 1
                                  ? "text-pnlNegative font-semibold"
                                  : "text-muted-foreground"
                              }
                            >
                              {Number(r.fallbackHours)} h
                            </span>
                          )}
                        </td>
                        <td className="text-foreground py-2 text-right font-mono">
                          {r.wsEvents}
                        </td>
                        <td
                          className={`py-2 text-right font-mono ${
                            r.wsReconnects > 0
                              ? "text-pnlNegative"
                              : "text-muted-foreground"
                          }`}
                        >
                          {r.wsReconnects}
                        </td>
                        <td
                          className={`py-2 text-right font-mono ${
                            r.diffCount > 0
                              ? "text-pnlNegative font-semibold"
                              : "text-muted-foreground"
                          }`}
                        >
                          {r.diffCount}
                        </td>
                        <td className="text-muted-foreground py-2 text-right font-mono">
                          {r.leaseAgeMinutes ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateBetaDiagnosticCard;
